/**
 * Maps backend error code prefixes to frontend error categories
 * Backend codes follow CAT_0xx convention (e.g., AUTH_001, VAL_002)
 */

import { AppError } from '../types/errors.interface';
import { ErrorCodes } from '../constants/errorCodes';

export type ErrorCategory = 'auth' | 'validation' | 'business' | 'network' | 'server';

/**
 * Determines the error category based on the error code prefix
 * 
 * @param code - Backend error code (e.g., 'AUTH_001', 'VAL_002')
 * @returns Error category for proper handling
 */
export function mapCodeToCategory(code: string): ErrorCategory {
  if (!code || typeof code !== 'string') {
    return 'server'; // Default for invalid codes
  }

  const prefix = code.split('_')[0].toUpperCase();

  switch (prefix) {
    case 'AUTH':
      return 'auth';
    case 'VAL':
    case 'REQ':
      return 'validation';
    case 'BUS':
      return 'business';
    case 'NET':
      return 'network';
    case 'SRV':
    case 'UNK':
    default:
      return 'server';
  }
}

/**
 * Creates an AppError object from a backend error code and message
 * 
 * @param code - Backend error code
 * @param message - Error message
 * @param field - Optional field name for validation errors
 * @param httpStatus - Optional HTTP status code
 * @returns Properly typed AppError
 */
export function createAppError(
  code: string,
  message: string,
  field?: string,
  httpStatus?: number
): AppError {
  const category = mapCodeToCategory(code);

  switch (category) {
    case 'auth':
      return {
        category: 'auth',
        code,
        message,
        field,
        httpStatus: httpStatus === 401 || httpStatus === 403 ? httpStatus : undefined,
        shouldLogout: httpStatus === 401 || code === 'AUTH_001',
        redirectToLogin: httpStatus === 401 || code === 'AUTH_001',
      };

    case 'validation':
      return {
        category: 'validation',
        code,
        message,
        field,
        fields: field ? [{ field, code, message }] : undefined,
      };

    case 'business':
      return {
        category: 'business',
        code,
        message,
        field,
      };

    case 'network':
      return {
        category: 'network',
        code,
        message,
        field,
        statusCode: httpStatus,
      };

    case 'server':
    default:
      return {
        category: 'server',
        code,
        message,
        field,
        statusCode: httpStatus,
      };
  }
}

/**
 * Checks if an error code represents an authentication error that requires logout
 * 
 * @param code - Backend error code
 * @returns True if user should be logged out
 */
export function shouldLogoutForErrorCode(code: string): boolean {
  // AUTH_UNAUTHENTICATED (401) - requires logout
  return code === ErrorCodes.AUTH_UNAUTHENTICATED;
}

/**
 * Checks if an error code represents an authorization error (403)
 * These errors should NOT trigger logout
 * 
 * @param code - Backend error code
 * @returns True if error is 403 Forbidden
 */
export function isUnauthorizedError(code: string): boolean {
  // AUTH_UNAUTHORIZED (403) - no logout
  return code === ErrorCodes.AUTH_UNAUTHORIZED;
}
